import { unwrapConnection } from "@utils/utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";

import { Small } from "@fronterahealth/frontera-ui-components";

import { ReportGoalType, ReportTargetType, useGetReportDomainsQuery } from "@api/graphql/types-and-hooks";
import { LayoutAside, LayoutBody, LayoutMain } from "@components/Layout";
import { GeneratingReassessmentScreen } from "@pages/ClinicalReportDetails/ReportPage/Components/GeneratingReassessmentScreen";
import { GoalInlineEditor } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalInlineEditor";
import { hasActiveFilters } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalsFilterPopover";
import { GoalsSidebar } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalsSidebar";
import { GoalsSidebarSearch } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalsSidebarSearch";
import { GoalsSidebarSkeleton } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalsSidebarSkeleton";
import { RefreshingIndicator } from "@pages/ClinicalReportDetails/ReportPage/Components/RefreshingIndicator";
import { TargetInlineEditor } from "@pages/ClinicalReportDetails/ReportPage/Components/TargetInlineEditor";
import { useManualRefreshProgramming } from "@pages/ClinicalReportDetails/ReportPage/hooks/useManualRefreshProgramming";
import { getGoalsOrderedByDomain } from "@pages/ClinicalReportDetails/ReportPage/utils/sortGoalsByDomain";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { ReportProgrammingProvider, useReportProgrammingData } from "@providers/ReportProgrammingProvider";
import { useReport } from "@providers/ReportProvider";

interface FlatItem {
  type: "goal" | "target";
  goal: ReportGoalType;
  target?: ReportTargetType;
}

const ProgrammingPageContent = () => {
  const { isProgressSummaryGenerating } = useClinicalReportDetailsData();
  const { report } = useReport();
  const [isSearching, setIsSearching] = useState(false);

  const { data: reportDomainsData } = useGetReportDomainsQuery(
    { reportId: report?.id ?? "" },
    { enabled: !!report?.id },
  );

  const { reassessmentGoalsQuery, selected, dispatchSelected, reassessmentGoalList, allGoals, filters } =
    useReportProgrammingData();

  const { isSyncing: isRefreshingProgramming } = useManualRefreshProgramming();

  // Sidebar + navigation (flatItems) render the filtered list.
  const goalList: ReportGoalType[] = reassessmentGoalList;

  // Selection lookups resolve against the UNFILTERED goal set (allGoals) so a
  // selected goal/target that's been filtered out of the sidebar — e.g. a
  // just-created custom goal/target excluded by an active date-range filter —
  // still renders in the editor pane instead of dropping to the empty state.
  const selectedGoal = selected.mode === "goal" ? allGoals.find((g) => g.id === selected.goalId) ?? null : null;

  const selectedTarget =
    selected.mode === "target"
      ? (() => {
          // Targets are nested under goals, so scan the unfiltered goal set.
          for (const goal of allGoals) {
            const targets = (unwrapConnection(goal.reporttargetSet) as ReportTargetType[]) || [];
            for (const target of targets) {
              if (goal.id === selected.goalId && target.id === selected.targetId) {
                return target;
              }
            }
          }
          return null;
        })()
      : null;

  const selectedGoalForTarget =
    selected.mode === "target" ? allGoals.find((g) => g.id === selected.goalId) ?? null : null;

  // Build a flat ordered list of goals and targets for navigation,
  // matching the domain-grouped, rankOrder-sorted order shown in the sidebar
  const flatItems: FlatItem[] = useMemo(() => {
    const reportDomains = reportDomainsData?.getReportDomains ?? [];
    const orderedGoals = getGoalsOrderedByDomain(goalList, reportDomains);

    const items: FlatItem[] = [];
    for (const goal of orderedGoals) {
      items.push({ type: "goal", goal });
      const targets = (unwrapConnection(goal.reporttargetSet) as ReportTargetType[]) || [];
      for (const target of targets) {
        if (!target.excludeFromReport) {
          items.push({ type: "target", goal, target });
        }
      }
    }
    return items;
  }, [goalList, reportDomainsData]);

  // Auto-select the first goal in the sidebar order whenever nothing is
  // selected and goals have loaded. Handles initial landing on /programming
  // (no empty state on first paint) and post-CLEAR transitions (cancel
  // create, delete) so the user is never dropped back to a blank pane.
  useEffect(() => {
    if (selected.mode !== "none") return;
    const firstGoal = flatItems.find((item) => item.type === "goal");
    if (!firstGoal) return;
    dispatchSelected({ type: "SELECT_GOAL", goalId: firstGoal.goal.id });
  }, [selected.mode, flatItems, dispatchSelected]);

  const currentIndex =
    selected.mode === "target"
      ? flatItems.findIndex((item) => item.type === "target" && item.target?.id === selected.targetId)
      : selected.mode === "goal"
        ? flatItems.findIndex((item) => item.type === "goal" && item.goal.id === selected.goalId)
        : -1;

  const navigateTo = useCallback(
    (item: FlatItem) => {
      if (item.type === "target" && item.target) {
        dispatchSelected({ type: "SELECT_TARGET", targetId: item.target.id, goalId: item.goal.id });
      } else {
        dispatchSelected({ type: "SELECT_GOAL", goalId: item.goal.id });
      }
    },
    [dispatchSelected],
  );

  const findItem = useCallback(
    (direction: -1 | 1, type?: "goal" | "target") => {
      if (currentIndex === -1) return undefined;
      const start = currentIndex + direction;
      for (let i = start; i >= 0 && i < flatItems.length; i += direction) {
        if (!type || flatItems[i].type === type) return flatItems[i];
      }
      return undefined;
    },
    [currentIndex, flatItems],
  );

  const handlePreviousGoal = useCallback(() => {
    const item = findItem(-1, "goal");
    if (item) navigateTo(item);
  }, [findItem, navigateTo]);

  const handleNextGoal = useCallback(() => {
    const item = findItem(1, "goal");
    if (item) navigateTo(item);
  }, [findItem, navigateTo]);

  const handlePreviousTarget = useCallback(() => {
    const item = findItem(-1, "target");
    if (item) navigateTo(item);
  }, [findItem, navigateTo]);

  const handleNextTarget = useCallback(() => {
    const item = findItem(1, "target");
    if (item) navigateTo(item);
  }, [findItem, navigateTo]);

  // Auto-advance after Approve: walk forward from the just-approved item and
  // land on the next *unapproved* item, regardless of type. The caller passes
  // the id of the item that was approved (captured at click time) so we can
  // detect when the user has manually navigated away during the toast delay
  // and skip the auto-advance to respect their manual selection.
  const handleAdvanceAfterApprove = useCallback(
    (fromItemId: string) => {
      const currentItemId =
        selected.mode === "goal" ? selected.goalId : selected.mode === "target" ? selected.targetId : null;
      // User manually navigated to a different item during the toast window —
      // their selection wins, no auto-advance.
      if (currentItemId !== fromItemId) return;

      const fromIndex = flatItems.findIndex((item) =>
        item.type === "target" ? item.target?.id === fromItemId : item.goal.id === fromItemId,
      );
      if (fromIndex === -1) return;

      for (let i = fromIndex + 1; i < flatItems.length; i++) {
        const item = flatItems[i];
        const isApproved = item.type === "target" ? !!item.target?.summaryReviewed : !!item.goal.summaryReviewed;
        if (!isApproved) {
          navigateTo(item);
          return;
        }
      }
    },
    [selected, flatItems, navigateTo],
  );

  const hasPreviousGoal = !!findItem(-1, "goal");
  const hasNextGoal = !!findItem(1, "goal");
  const hasPreviousTarget = !!findItem(-1, "target");
  const hasNextTarget = !!findItem(1, "target");

  const navigation = {
    onPreviousGoal: hasPreviousGoal ? handlePreviousGoal : undefined,
    onNextGoal: hasNextGoal ? handleNextGoal : undefined,
    onPreviousTarget: hasPreviousTarget ? handlePreviousTarget : undefined,
    onNextTarget: hasNextTarget ? handleNextTarget : undefined,
    onAdvanceAfterApprove: handleAdvanceAfterApprove,
  };

  // Navigate to select programming only when no goals exist AND no filters are active.
  // If filters are applied, the list may be empty due to filtering — not because goals are absent.
  const shouldNavigateToSelectProgramming =
    !reassessmentGoalList.length &&
    !hasActiveFilters(filters) &&
    reassessmentGoalsQuery.isSuccess &&
    !reassessmentGoalsQuery.isRefetching;

  if (shouldNavigateToSelectProgramming) {
    return <Navigate to="../select-programming" relative="path" replace />;
  }

  if (isProgressSummaryGenerating) {
    return <GeneratingReassessmentScreen />;
  }

  return (
    <LayoutBody className="px-0 gap-0">
      <LayoutAside className="w-86 flex-initial bg-white border-r border-border-disabled">
        {reassessmentGoalsQuery.isLoading ? (
          <GoalsSidebarSkeleton />
        ) : isSearching ? (
          <GoalsSidebarSearch
            reportId={report?.id ?? ""}
            goalList={goalList}
            onSearchComplete={() => setIsSearching(false)}
          />
        ) : (
          <GoalsSidebar reportId={report?.id ?? ""} goalList={goalList} onSearch={() => setIsSearching(true)} />
        )}
      </LayoutAside>
      <LayoutMain>
        {/* Main Content: draft form OR goal/target editor / empty state (never both) */}
        {(() => {
          if (isRefreshingProgramming) {
            return (
              <div className="flex-1 overflow-y-auto">
                <div className="flex h-full items-center justify-center">
                  <RefreshingIndicator />
                </div>
              </div>
            );
          }
          if (selected.mode === "goal" && selectedGoal) {
            // No outer overflow-y-auto: the editor manages its own scroll so the
            // ApprovalBanner can sit OUTSIDE the scrolling area and stay solidly
            // pinned without the position:sticky bounce on fast scrolls.
            return (
              <div className="flex flex-1 flex-col min-h-0">
                <GoalInlineEditor goal={selectedGoal} navigation={navigation} />
              </div>
            );
          }
          if (selected.mode === "target" && selectedTarget && selectedGoalForTarget) {
            return (
              <div className="flex flex-1 flex-col min-h-0">
                <TargetInlineEditor target={selectedTarget} goal={selectedGoalForTarget} navigation={navigation} />
              </div>
            );
          }
          return (
            <div className="flex-1 overflow-y-auto">
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <Small className="text-text-secondary">Select a goal or target from the sidebar to get started</Small>
                </div>
              </div>
            </div>
          );
        })()}
      </LayoutMain>
    </LayoutBody>
  );
};

export const ProgrammingPage: React.FC = () => {
  const { reportQuery } = useReport();
  return (
    <ReportProgrammingProvider reportQuery={reportQuery}>
      <ProgrammingPageContent />
    </ReportProgrammingProvider>
  );
};
